import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {Switch} from '@/web-components';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';
import {HorizontalFlexWrapper} from '../salesforce/app_salesforce_style';
import {SelectionElementLabel} from '../../../project-tab/projects/project-settings/ProjectSettings_styled';
import {createToast} from '../../../shared/components/toasts/toast';
import {useIntl} from 'react-intl';
import DirectApi from '../../../../directApi';

export const HUBSPOT_LINKED_FIELD_ORDER_WITH_DATES = ['dealName', 'dealAmount', 'dealDates'];
export const HUBSPOT_LINKED_FIELD_ORDER_LEGACY = ['dealName', 'dealAmount'];

const HUBSPOT_SYNC_DIRECTION_LABEL_IDS = {
	dealName: 'settings.app_catalog.hubspot.sync_direction.deal_name',
	dealAmount: 'settings.app_catalog.hubspot.sync_direction.deal_amount',
	dealDates: 'settings.app_catalog.hubspot.sync_direction.deal_dates',
};

const defaultHubspotLinkedField = (intl, key) => ({
	linked: false,
	text: intl.formatMessage({id: HUBSPOT_SYNC_DIRECTION_LABEL_IDS[key]}),
});

/** Request body for hubspot `linked_fields` PUT — which fields are linked and their labels (no sync-direction flags). */
export function buildHubspotLinkedFieldsPutBody(linkedFields) {
	const body = {};
	Object.keys(linkedFields).forEach(fieldKey => {
		if (linkedFields[fieldKey].linked) {
			body[fieldKey] = linkedFields[fieldKey].text;
		}
	});
	return body;
}

export function normalizeHubspotLinkedFields(raw, intl, includeDealDatesField) {
	const merged = {...(raw || {})};
	const keysWithDefaults = includeDealDatesField ? HUBSPOT_LINKED_FIELD_ORDER_WITH_DATES : HUBSPOT_LINKED_FIELD_ORDER_LEGACY;

	for (const key of keysWithDefaults) {
		const fallback = defaultHubspotLinkedField(intl, key);
		if (merged[key] == null) {
			merged[key] = {...fallback};
		} else {
			const existing = merged[key];
			merged[key] = {
				...existing,
				text: existing.text || fallback.text,
				linked: existing.linked === true,
			};
		}
	}

	if (!includeDealDatesField && merged.dealDates != null) {
		const existing = merged.dealDates;
		const fallback = defaultHubspotLinkedField(intl, 'dealDates');
		merged.dealDates = {
			...existing,
			text: existing.text || fallback.text,
			linked: existing.linked === true,
		};
	}

	const primaryOrder = includeDealDatesField ? HUBSPOT_LINKED_FIELD_ORDER_WITH_DATES : HUBSPOT_LINKED_FIELD_ORDER_LEGACY;
	const ordered = {};
	for (const key of primaryOrder) {
		if (merged[key] != null) {
			ordered[key] = merged[key];
		}
	}
	for (const key of Object.keys(merged)) {
		if (ordered[key] === undefined) {
			ordered[key] = merged[key];
		}
	}
	return ordered;
}

const genericIntegrationLinkedFields = ({
	integrationName,
	includeHubspotDealDatesField = false,
	onHubspotLinkedFieldsUpdated,
	reloadTrigger = 0,
}) => {
	const intl = useIntl();
	const [linkedFields, setLinkedFields] = useState(null);
	const onHubspotLinkedFieldsUpdatedRef = useRef(onHubspotLinkedFieldsUpdated);
	onHubspotLinkedFieldsUpdatedRef.current = onHubspotLinkedFieldsUpdated;

	const loadLinkedFields = useCallback(() => {
		DirectApi.Fetch(`${integrationName}/linked_fields`).then(json => {
			let fields = json.linkedFields;
			if (integrationName === 'hubspot') {
				fields = normalizeHubspotLinkedFields(fields ?? {}, intl, includeHubspotDealDatesField);
			}
			setLinkedFields(fields);
			if (integrationName === 'hubspot') {
				onHubspotLinkedFieldsUpdatedRef.current?.(fields);
			}
		});
	}, [integrationName, intl, includeHubspotDealDatesField]);

	useEffect(() => {
		loadLinkedFields();
	}, [loadLinkedFields, reloadTrigger]);

	const handleSwitchChange = (value, field) => {
		const newLinkedFields = {...linkedFields};
		newLinkedFields[field].linked = !value;

		const body = buildHubspotLinkedFieldsPutBody(newLinkedFields);

		const headers = new Headers();
		headers.append('Content-Type', 'application/json');

		const init = {
			headers: headers,
			credentials: 'include',
			method: 'PUT',
			body: JSON.stringify(body),
		};

		return fetch(DirectApi.graphqlServerEndpoint(`hubspot/linked_fields`), init).then(() => {
			setLinkedFields(newLinkedFields);
			onHubspotLinkedFieldsUpdatedRef.current?.(newLinkedFields);
			createToast({
				duration: 2000,
				message: intl.formatMessage({id: 'common.changes_saved'}),
			});
		});
	};

	const hubspotRenderKeys =
		integrationName === 'hubspot'
			? includeHubspotDealDatesField
				? HUBSPOT_LINKED_FIELD_ORDER_WITH_DATES
				: HUBSPOT_LINKED_FIELD_ORDER_LEGACY
			: null;

	return linkedFields ? (
		<>
			{(hubspotRenderKeys || Object.keys(linkedFields)).map(field =>
				linkedFields[field] ? (
					<LinkedFieldsWrapper key={field}>
						<HorizontalFlexWrapper>
							<Switch
								checked={linkedFields[field].linked}
								onChange={value => handleSwitchChange(value, field)}
								cy={integrationName === 'hubspot' ? `hubspot-linked-field-${field}` : undefined}
							/>
							<SelectionElementLabel>{linkedFields[field].text}</SelectionElementLabel>
						</HorizontalFlexWrapper>
					</LinkedFieldsWrapper>
				) : null
			)}
		</>
	) : (
		<InlineLoader />
	);
};

const LinkedFieldsWrapper = styled.div`
	margin: 8px 0;
`;

export default genericIntegrationLinkedFields;
